import React from "react";
import styles from "./SuffolkLockerSelect.module.css";

/** Physical cabinet for suffolkcounty: 1–4 left, 5–8 right, screen, then 9 and 10. */
const SUFFOLK_SLOTS = {
  left: [
    { number: 1, sizeLetter: "S", handle: "right" },
    { number: 2, sizeLetter: "S", handle: "right" },
    { number: 3, sizeLetter: "S", handle: "right" },
    { number: 4, sizeLetter: "S", handle: "right" },
  ],
  right: [
    { number: 5, sizeLetter: "S", handle: "left" },
    { number: 6, sizeLetter: "S", handle: "left" },
    { number: 7, sizeLetter: "S", handle: "left" },
    { number: 8, sizeLetter: "S", handle: "left" },
  ],
  lower: [
    { number: 9, sizeLetter: "M", tall: false, handle: "right" },
    { number: 10, sizeLetter: "XL", tall: true, handle: "right" },
  ],
};

function getSizeRecord(sizes, letter) {
  let record = sizes.find((item) => item.size === letter);
  if (!record && letter === "XL") {
    record = sizes.find((item) => item.size === "L");
  }
  if (!record && letter === "M") {
    record = sizes.find((item) => item.size === "L");
  }
  return record;
}

function isLockerAvailable(status) {
  return (status || "").toLowerCase() === "available";
}

function lockerTail(number) {
  const tail = String(number || "").match(/(\d+)$/);
  return tail ? tail[1] : "";
}

function sizeIdFor(sizes, letter) {
  const record = sizes.find((item) => item.size === letter);
  return record ? String(record._id) : "";
}

/**
 * Doors 1–8 are the small lockers (S-1 … S-8).
 * Doors 9 and 10 are the two large lockers. On this site those records are
 * L-1 and L-2 (size L), not lockers numbered 9 and 10.
 */
function assignSuffolkLockers(siteLockers, sizes) {
  const lockers = siteLockers || [];
  const used = new Set();
  const bySlot = {};

  const take = (locker) => {
    if (!locker) return null;
    const id = String(locker._id);
    if (used.has(id)) return null;
    used.add(id);
    return locker;
  };

  for (let n = 1; n <= 8; n += 1) {
    const num = String(n);
    const small =
      lockers.find((locker) => locker.number === `S-${num}`) ||
      lockers.find((locker) => locker.number === num);
    bySlot[n] = take(small);
  }

  const remaining = () => lockers.filter((locker) => !used.has(String(locker._id)));

  const pickLarge = (letters) => {
    for (const letter of letters) {
      const sizeId = sizeIdFor(sizes, letter);
      const bySize = remaining().find(
        (locker) => sizeId && String(locker.size_id) === sizeId
      );
      if (bySize) return bySize;
      const byName = remaining().find((locker) =>
        new RegExp(`^${letter}-`, "i").test(locker.number || "")
      );
      if (byName) return byName;
    }
    return null;
  };

  const larges = remaining()
    .filter((locker) => /^(M|L|XL)-/i.test(locker.number || ""))
    .sort(
      (a, b) =>
        (parseInt(lockerTail(a.number), 10) || 0) -
        (parseInt(lockerTail(b.number), 10) || 0)
    );

  bySlot[9] = take(pickLarge(["M", "L"]) || larges[0]);
  bySlot[10] = take(pickLarge(["XL", "L"]) || larges.find((locker) => !used.has(String(locker._id))));
  return bySlot;
}

function doorInputKey(value) {
  const num = Number(String(value || "").trim());
  return Number.isFinite(num) && num > 0 ? String(num) : "";
}

function SuffolkDoor({
  slot,
  sizes,
  locker,
  doorInputs,
  busy,
  selectedLockerId,
  onSelectLocker,
  allowOccupied,
  colorByStatus,
  sensorColors,
  viewOnly,
  className,
}) {
  const sizeRecord = getSizeRecord(sizes, slot.sizeLetter);
  const available = locker ? isLockerAvailable(locker.status) : false;
  const statusLabel = available ? "AVAILABLE" : "OCCUPIED";
  const isSelected = locker && selectedLockerId === locker._id;
  const isDisabled = !locker || busy || (!allowOccupied && !available);
  const inputKey = sensorColors
    ? doorInputKey(locker?.relay)
    : doorInputKey(locker?.relay) || doorInputKey(slot.number);
  const doorState = inputKey ? doorInputs?.[inputKey] : "";
  const numberClass = sensorColors
    ? doorState === "open"
      ? styles.numberOpen
      : doorState === "closed"
      ? styles.numberClosedGreen
      : ""
    : colorByStatus
    ? available
      ? styles.numberAvailable
      : styles.numberOccupiedStatus
    : doorState === "open"
    ? styles.numberOpen
    : doorState === "closed"
    ? styles.numberClosed
    : "";

  const handleOnLeft = slot.handle === "left";
  const doorClass = `${styles.door} ${
    handleOnLeft ? styles.handleLeft : styles.handleRight
  } ${className || ""} ${available ? styles.doorAvailable : styles.doorOccupied} ${
    !viewOnly && allowOccupied && locker ? styles.doorClickable : ""
  } ${!viewOnly && isSelected ? styles.doorSelected : ""}`;
  const label = `Locker ${slot.number}, ${statusLabel}${
    isSelected ? ", selected" : ""
  }`;
  const face = (
    <>
      <span className={styles.doorLabel}>
        <span className={`${styles.doorNumber} ${numberClass}`}>{slot.number}</span>
        {!viewOnly && <span className={styles.doorStatus}>{statusLabel}</span>}
      </span>
      <span className={styles.doorHandle} aria-hidden="true" />
    </>
  );

  if (viewOnly) {
    return (
      <div className={doorClass} aria-label={label}>
        {face}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={doorClass}
      disabled={isDisabled}
      onClick={() => onSelectLocker?.(locker, sizeRecord)}
      aria-label={label}
      aria-pressed={isSelected}
    >
      {face}
    </button>
  );
}

const SuffolkLockerSelect = ({
  sizes,
  siteLockers,
  doorInputs,
  busy,
  selectedLockerId,
  onSelectLocker,
  allowOccupied = false,
  colorByStatus = false,
  sensorColors = false,
  viewOnly = false,
}) => {
  const lockersBySlot = assignSuffolkLockers(siteLockers, sizes);
  const doorProps = {
    sizes,
    doorInputs,
    busy,
    selectedLockerId,
    onSelectLocker,
    allowOccupied,
    colorByStatus,
    sensorColors,
    viewOnly,
  };

  return (
    <div className={`${styles.wrap} ${viewOnly ? styles.viewOnly : ""}`}>
      <div className={styles.cabinet}>
        <div className={styles.top}>
          <div className={styles.column}>
            {SUFFOLK_SLOTS.left.map((slot) => (
              <SuffolkDoor
                key={slot.number}
                slot={slot}
                locker={lockersBySlot[slot.number]}
                {...doorProps}
              />
            ))}
          </div>
          <div className={styles.column}>
            {SUFFOLK_SLOTS.right.map((slot) => (
              <SuffolkDoor
                key={slot.number}
                slot={slot}
                locker={lockersBySlot[slot.number]}
                {...doorProps}
              />
            ))}
          </div>
        </div>

        <div className={styles.screenBay} aria-hidden="true">
          <div className={styles.bezel}>
            <div className={styles.screen}>
              <div className={styles.brand}>InVid</div>
              <div className={styles.touch}>Touch to begin</div>
            </div>
          </div>
        </div>

        {SUFFOLK_SLOTS.lower.map((slot) => (
          <SuffolkDoor
            key={slot.number}
            slot={slot}
            locker={lockersBySlot[slot.number]}
            {...doorProps}
          />
        ))}
      </div>
    </div>
  );
};

export default SuffolkLockerSelect;
